import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import {
  Card,
  Chips,
  Muted,
  Select,
  Spinner,
  Tag,
  Text,
  datedName,
  fmtDate,
  saveTextFile,
  useAsync
} from '../../ui.jsx';

const statusTone = {
  submitted: 'mid',
  under_review: '',
  offered: 'ok',
  rejected: 'no',
  withdrawn: ''
};

const STATUSES = ['All', 'submitted', 'under_review', 'offered', 'rejected', 'withdrawn'];

export default function AdminApplications() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get('status') || 'All');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [sort, setSort] = useState('newest');
  const [exporting, setExporting] = useState(false);

  const { data, loading, error } = useAsync(
    () => adminApi.applications({ status, q: term, sort }),
    [status, term, sort]
  );

  const rows = data?.applications || [];

  const exportCsv = async () => {
    // Fetched by the app rather than linked, so the access token goes with it.
    // A plain link or window.location.href here is unauthenticated and 401s.
    setExporting(true);
    try {
      const search = new URLSearchParams();
      if (status !== 'All') search.set('status', status);
      if (term) search.set('q', term);
      const csv = await adminApi.exportCsv(Object.fromEntries(search));
      saveTextFile(csv, datedName('applications', 'csv'));
    } catch (err) {
      notice.fromError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="row sp">
        <div>
          <h2 style={{ margin: 0 }}>Applications</h2>
          <Muted>{data ? `${data.total} applications` : 'Loading'}</Muted>
        </div>
        <button type="button" className="btn s sm" onClick={exportCsv} disabled={exporting}>
          {exporting ? 'Preparing...' : 'Export CSV'}
        </button>
      </div>

      <Card>
        <form
          className="row"
          style={{ flexWrap: 'nowrap', alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            setTerm(q.trim());
          }}
        >
          <div style={{ flex: 1 }}>
            <Text
              label="Search"
              placeholder="Name, email or programme"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <button className="btn" type="submit">Search</button>
        </form>

        <div style={{ marginTop: 12 }}>
          <Chips
            label="Status"
            options={STATUSES.map((s) => ({ value: s, label: s === 'All' ? 'All' : s.replace(/_/g, ' ') }))}
            value={status}
            onChange={setStatus}
          />
        </div>

        <div style={{ marginTop: 10, maxWidth: 220 }}>
          <Select label="Sort by" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="name">Name</option>
            <option value="aggregate">Aggregate, best first</option>
          </Select>
        </div>
      </Card>

      {loading ? <Spinner label="Loading applications" /> : null}
      {error ? <div className="note">We could not load applications. Please try again.</div> : null}

      {data && !loading ? (
        rows.length === 0 ? (
          <Card>
            <Muted>No applications match this filter.</Muted>
          </Card>
        ) : (
          <Card>
            <div className="scroll-x">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Programmes</th>
                    <th className="num">Aggregate</th>
                    <th>Submitted</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <b>{row.name}</b>
                        <div className="mu">{row.email}</div>
                      </td>
                      <td>{row.programmes.join(', ') || <span className="mu">None chosen</span>}</td>
                      <td className="num">{row.aggregate ?? '--'}</td>
                      <td>{fmtDate(row.submittedAt)}</td>
                      <td>
                        {row.paid ? (
                          <Tag tone="ok">Paid</Tag>
                        ) : (
                          <Tag tone="mid">Not paid</Tag>
                        )}
                      </td>
                      <td>
                        <Tag tone={statusTone[row.status]}>{row.status.replace(/_/g, ' ')}</Tag>
                      </td>
                      <td>
                        <Link to={`/admin/applications/${row.id}`} className="btn sm" style={{ textDecoration: 'none' }}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      ) : null}
    </>
  );
}
